import React from 'react';
import { Container, Alert, Button } from 'react-bootstrap';
import { useRouteError, Link, isRouteErrorResponse } from 'react-router-dom';

const VerifyInvitationError: React.FC = () => {
  const error = useRouteError();

  const getMessage = (): string => {
    const fallback = 'An unknown error occurred';

    if (isRouteErrorResponse(error)) return error.statusText || fallback;
    if (error instanceof Error) return error.message || fallback;

    return fallback;
  };

  const errorStatus = isRouteErrorResponse(error) ? error.status : undefined;

  const getErrorContent = () => {
    switch (errorStatus) {
      case 400:
        return {
          title: 'Invalid Invitation Link',
          message:
            'The invitation link is missing required information. Please check the link and try again.',
        };

      case 404:
        return {
          title: 'Invitation Not Found',
          message:
            'This invitation could not be found. Please reach out to an admin for assistance.',
        };

      case 409:
        return {
          title: 'Account Already Exists',
          message:
            'You already have an account with this email address. Please login instead of registering again.',
        };

      case 410: {
        // Check the error message to distinguish between expired and already used
        const errorMessage = getMessage();
        if (errorMessage.includes('already used')) {
          return {
            title: 'Invitation Already Used',
            message:
              'This invitation has already been used and cannot be used again. Please reach out to an admin for assistance.',
          };
        } else {
          return {
            title: 'Invitation Expired',
            message:
              'This invitation has expired. Please reach out to an admin for a new invitation.',
          };
        }
      }

      case 500:
        return {
          title: 'Server Error',
          message:
            'We encountered a server error while verifying your invitation. Please try again later.',
        };

      default:
        return {
          title: 'Invitation Error',
          message: getMessage(),
        };
    }
  };

  const { title, message } = getErrorContent();
  const variant = errorStatus === 409 ? 'info' : 'danger';
  const showLoginButton = errorStatus === 409;

  return (
    <Container className="py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <Alert variant={variant} className="text-center">
            <Alert.Heading>{title}</Alert.Heading>
            <p className="mb-3">{message}</p>

            {showLoginButton && (
              <>
                <hr />
                <Link to="/portal/login">
                  <Button variant="primary" size="sm">
                    Go to Login
                  </Button>
                </Link>
              </>
            )}
          </Alert>
        </div>
      </div>
    </Container>
  );
};

export default VerifyInvitationError;
